import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import RequireAuth from "@/components/RequireAuth";
import PlatformGuard from "@/components/PlatformGuard";
import { isWebPlatform } from "@/lib/platform";
import ExitConfirmDialog from "@/components/ExitConfirmDialog";
import OfflineBanner from "@/components/OfflineBanner";
import { Loader2 } from "lucide-react";
import { useGlobalBackButton } from "@/hooks/useBackButton";
import { useOAuthDeepLink } from "@/hooks/useOAuthDeepLink";
import { supabase } from "@/lib/supabase";
import { analytics } from "@/lib/analytics";
import { useRefreshAppConfigOnResume } from "@/lib/billing/appConfig";
import { useRefreshOwnTierOnResume } from "@/hooks/useSubscription";
import { CapacitorUpdater } from '@capgo/capacitor-updater';

// Eager: tiny entry pages
import SplashScreen from "./pages/SplashScreen";
import AuthScreen from "./pages/AuthScreen";

// Lazy: everything else
const RoleSelection = lazy(() => import("./pages/RoleSelection"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ParentalConsent = lazy(() => import("./pages/ParentalConsent"));
const AccountDeleted = lazy(() => import("./pages/AccountDeleted"));
const PlayerLayout = lazy(() => import("./components/PlayerLayout"));
const PlayerOnboarding = lazy(() => import("./pages/PlayerOnboarding"));
const PlayerHome = lazy(() => import("./pages/PlayerHome"));
const StrengthCard = lazy(() => import("./pages/StrengthCard"));
const MyVideos = lazy(() => import("./pages/MyVideos"));
const VideoPlayer = lazy(() => import("./pages/VideoPlayer"));
const Reports = lazy(() => import("./pages/Reports"));
const PlayerAccount = lazy(() => import("./pages/PlayerAccount"));
const PlayerSettings = lazy(() => import("./pages/PlayerSettings"));
const PlayerReferrals = lazy(() => import("./pages/PlayerReferrals"));
const PlayerViewProfile = lazy(() => import("./pages/PlayerViewProfile"));
const DiscoverPlayers = lazy(() => import("./pages/DiscoverPlayers"));
const TrialsPage = lazy(() => import("./pages/TrialsPage"));
const AcademyPrivacySettings = lazy(() => import("./pages/player/AcademyPrivacySettings"));

const ScoutLayout = lazy(() => import("./components/ScoutLayout"));
const ScoutOnboarding = lazy(() => import("./components/ScoutOnboarding"));
const ScoutFindTalent = lazy(() => import("./pages/ScoutFindTalent"));
const ScoutShortlists = lazy(() => import("./pages/ScoutShortlists"));
const SavedReports = lazy(() => import("./pages/SavedReports"));
const AIAgents = lazy(() => import("./pages/AIAgents"));
const ScoutConnect = lazy(() => import("./pages/ScoutConnect"));
const ScoutSettings = lazy(() => import("./pages/ScoutSettings"));
const ScoutAthleteProfile = lazy(() => import("./pages/ScoutAthleteProfile"));
const ScoutCompare = lazy(() => import("./pages/ScoutCompare"));
const ScoutFeed = lazy(() => import("./pages/ScoutFeed"));

const AcademyLayout = lazy(() => import("./components/AcademyLayout"));
const AcademyOnboarding = lazy(() => import("./pages/AcademyOnboarding"));
const AcademyHome = lazy(() => import("./pages/AcademyHome"));
const AcademyReports = lazy(() => import("./pages/AcademyReports"));
const AcademyTrials = lazy(() => import("./pages/AcademyTrials"));
const AcademyTab = lazy(() => import("./pages/AcademyTab"));
const AcademyPlayerProfile = lazy(() => import("./pages/AcademyPlayerProfile"));
// Academy mobile "Add Session Video" — full-screen route opened from the
// bottom-nav Add Video button. Uploads into the shared `videos` table.
const AcademyAddVideo = lazy(() => import("./pages/academy/AcademyAddVideo"));
// Web-only academy shell + pages (mounted only when isWebPlatform()).
const AcademyWebLayout = lazy(() => import("./components/academy/AcademyWebLayout"));
const AcademyDashboardWeb = lazy(() => import("./pages/academy/AcademyDashboardWeb"));
const AcademyPlayersWeb = lazy(() => import("./pages/academy/AcademyPlayersWeb"));
const AcademyReportsWeb = lazy(() => import("./pages/academy/AcademyReportsWeb"));
const PlayerReportDetail = lazy(() => import("./pages/academy/PlayerReportDetail"));
const AcademyPromoteWeb = lazy(() => import("./pages/academy/AcademyPromoteWeb"));
const AcademyPromoteMobile = lazy(() => import("./pages/academy/AcademyPromoteMobile"));
// Tournaments — web academy feature (list → detail → match analysis).
const AcademyTournamentsWeb = lazy(() => import("./pages/academy/AcademyTournamentsWeb"));
const TournamentDetailWeb = lazy(() => import("./pages/academy/TournamentDetailWeb"));
const MatchAnalysisWeb = lazy(() => import("./pages/academy/MatchAnalysisWeb"));
const AcademySettingsWeb = lazy(() => import("./pages/academy/AcademySettingsWeb"));
const AcademyBillingWeb = lazy(() => import("./pages/academy/AcademyBillingWeb"));
// Player → academy video approval surfaces (web panel + mobile full-screen).
const VideoApprovalsPanel = lazy(() => import("./components/academy/VideoApprovalsPanel"));
const VideoApprovalsMobile = lazy(() => import("./components/academy/VideoApprovalsMobile"));

// AI Lab — web-only nested tree under /academy/ailab. Selected player
// is held in AILabWebLayout's local state and shared with leaf pages
// via useOutletContext, so it persists across tab changes without
// touching the URL.
const AILabWebLayout = lazy(() => import("./components/ailab/AILabWebLayout"));
const AILabBowlingWeb = lazy(() => import("./pages/ailab/AILabBowlingWeb"));
const AILabBattingWeb = lazy(() => import("./pages/ailab/AILabBattingWeb"));
const AILabKeepingWeb = lazy(() => import("./pages/ailab/AILabKeepingWeb"));
const AILabFieldingWeb = lazy(() => import("./pages/ailab/AILabFieldingWeb"));
const AILabRadarWeb = lazy(() => import("./pages/ailab/AILabRadarWeb"));
const AILabComparisonWeb = lazy(() => import("./pages/ailab/AILabComparisonWeb"));
const AILabAskSportsBio = lazy(() => import("./pages/ailab/AILabAskSportsBio"));

// AI Lab — mobile-only screens. The grid home (AILabMobile) sits
// inside the AcademyLayout (bottom-nav stays visible); each feature
// screen is a full-screen push route with its own back button. Player
// selection persists across all screens via src/lib/aiLabMobileStore.
const AILabMobile = lazy(() => import("./pages/ailab/AILabMobile"));
const AILabBowlingMobile = lazy(() => import("./pages/ailab/mobile/AILabBowlingMobile"));
const AILabBattingMobile = lazy(() => import("./pages/ailab/mobile/AILabBattingMobile"));
const AILabKeepingMobile = lazy(() => import("./pages/ailab/mobile/AILabKeepingMobile"));
const AILabFieldingMobile = lazy(() => import("./pages/ailab/mobile/AILabFieldingMobile"));
const AILabRadarMobile = lazy(() => import("./pages/ailab/mobile/AILabRadarMobile"));
const AILabComparisonMobile = lazy(() => import("./pages/ailab/mobile/AILabComparisonMobile"));
const AILabAskSportsBioMobile = lazy(() => import("./pages/ailab/mobile/AILabAskSportsBioMobile"));

// Parent persona — lazy-loaded. Native uses ParentLayout (bottom nav);
// web uses ParentPortalWeb (sidebar). Routes diverge based on
// isWebPlatform() at the route element level.
const ParentLayout = lazy(() => import("./components/ParentLayout"));
const ParentAuth = lazy(() => import("./pages/parent/ParentAuth"));
const ParentOnboarding = lazy(() => import("./pages/parent/ParentOnboarding"));
const ParentHome = lazy(() => import("./pages/parent/ParentHome"));
const ParentVideos = lazy(() => import("./pages/parent/ParentVideos"));
const ParentProgress = lazy(() => import("./pages/parent/ParentProgress"));
const ParentNotes = lazy(() => import("./pages/parent/ParentNotes"));
const ParentReports = lazy(() => import("./pages/parent/ParentReports"));
const ParentSettings = lazy(() => import("./pages/parent/ParentSettings"));
const ParentPortalWeb = lazy(() => import("./pages/parent/ParentPortalWeb"));

const NotFound = lazy(() => import("./pages/NotFound"));
// StrengthCard redesign preview (mock data, no auth). Safe to remove once
// the redesign is signed off.
const StrengthCardDemo = lazy(() => import("./pages/StrengthCardDemo"));
const Pricing = lazy(() => import("./pages/Pricing"));
// New academy-marketing pricing page (web-only). On native, /pricing
// still serves the in-app billing surface above.
const PricingPage = lazy(() => import("./pages/PricingPage"));
const UpgradePage = lazy(() => import("./pages/web/UpgradePage"));
const PaymentSuccess = lazy(() => import("./pages/web/PaymentSuccess"));
const AdminBilling = lazy(() => import("./pages/web/AdminBilling"));

// 401-aware retry policy. Supabase's auto-refresh handles most cases,
// but there's a small window between token expiry and refresh where an
// in-flight query can come back with a JWT error. We use the React
// Query global retry (Approach A from the pre-launch audit) because
// it's a single declarative configuration that covers every
// useQuery / useInfiniteQuery in the app without per-callsite changes.
// On a detected JWT/auth error we trigger refreshSession() and let
// React Query do one retry; non-auth errors fall through to React
// Query's default short-retry behaviour.
function isAuthError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { message?: string; code?: string; status?: number };
  // PostgREST returns PGRST301 when the JWT is expired or missing.
  // Supabase Auth returns 401 on bad/expired session.
  if (e.code === "PGRST301") return true;
  if (e.status === 401) return true;
  if (typeof e.message === "string") {
    const m = e.message.toLowerCase();
    if (m.includes("jwt") || m.includes("not authenticated") || m.includes("invalid session")) {
      return true;
    }
  }
  return false;
}

// React Query global defaults.
//
// staleTime + refetchOnMount + refetchOnWindowFocus are the biggest
// perceived-perf knobs in an app like this. React Query's defaults
// (staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: true)
// mean every navigation triggers a refetch — feed, follow lists,
// profile data all re-hit Supabase as the user moves between screens.
// For our data shape (feed is sub-minute stale at worst; tiers /
// counts change infrequently; profile data is mostly stable), the
// settings below trade staleness tolerance for snappy navigation.
//
// Individual hooks still set TIGHTER staleTimes where they care
// (e.g. useScoringEligibility 10s, useFeed 30s) — those overrides
// take precedence and remain in effect.
//
// The `retry` function below is PRESERVED from the pre-launch audit
// (batch 2) — it handles the JWT-expired race where Supabase's
// auto-refresh hasn't landed before a request fires. Modifying it
// without preserving the auth-error branch would regress that fix.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default to 5-minute freshness. Hooks that care about being
      // fresher (feeds, eligibility checks) set their own staleTime.
      staleTime: 5 * 60 * 1000,
      // Keep cached entries for 10 minutes after last consumer
      // unmounts — covers tab switches without re-fetching.
      gcTime: 10 * 60 * 1000,
      // Don't refetch when the user just navigated to a screen that
      // already has cached data. The big perceived-perf win.
      refetchOnMount: false,
      // Don't refetch when the user switches Chrome tab and comes
      // back. Mobile-first app; tab focus is mostly noise.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (isAuthError(error)) {
          // Kick off a session refresh on the first auth failure, then
          // allow exactly one retry. We don't await here — the retry's
          // delay (React Query's default exponential backoff) gives the
          // refresh time to land before the next attempt fires.
          if (failureCount === 0) {
            void supabase.auth.refreshSession();
            return true;
          }
          return false;
        }
        // Default behaviour for non-auth errors: up to 2 retries.
        return failureCount < 2;
      },
    },
    mutations: {
      // Mutations are user-initiated; failing twice silently is worse
      // than surfacing the error so the user can decide to retry.
      retry: 0,
    },
  },
});

// Full-screen fallback that matches the app's dark background so there's no
// white/rectangle flash while lazy routes swap.
const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

/**
 * Thin wrapper that mounts the global Capacitor back-button listener inside
 * the Router tree so it can call navigate()/read useLocation().
 */
const AppShell = ({ children }: { children: React.ReactNode }) => {
  useGlobalBackButton();
  // OAuth callback deep-link handler — listens for
  // `com.sportsbio.app://auth-callback` and exchanges the code for a
  // session. Native-only; no-op on web.
  useOAuthDeepLink();
  // Re-read app_config (billing_mode) on every resume so a flip made at
  // /admin/billing reaches running installs without a cold start.
  useRefreshAppConfigOnResume();
  // Same for the user's own subscription tier (PlanBadge, ad-free state),
  // which changes server-side on web payments, renewals and expiry.
  useRefreshOwnTierOnResume();
  // GA4 SPA page-view tracking — fires on every route change (gtag is
  // configured with send_page_view:false in index.html).
  const location = useLocation();
  useEffect(() => {
    analytics.pageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    void CapacitorUpdater.notifyAppReady();
  }, []);

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sonner />
      <OfflineBanner />
      <BrowserRouter>
        <AuthProvider>
          <AppShell>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<SplashScreen />} />
              <Route path="/strength-card-demo" element={<StrengthCardDemo />} />
              <Route path="/auth" element={<AuthScreen />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/parental-consent" element={<ParentalConsent />} />
              <Route path="/account-deleted" element={<AccountDeleted />} />
              <Route path="/role-select" element={<RoleSelection />} />

              {/* Player routes — gated. PlatformGuard ensures the web build
                  (Academy-only surface) never exposes player UI; native
                  passes through. */}
              <Route element={<PlatformGuard allow="native" />}>
              <Route element={<RequireAuth role="player" />}>
                <Route path="/player" element={<PlayerLayout />}>
                  <Route path="onboarding" element={<PlayerOnboarding />} />
                  <Route path="home" element={<PlayerHome />} />
                  <Route path="trials" element={<TrialsPage />} />
                  <Route path="strength-card" element={<StrengthCard />} />
                  {/* MyVideos no longer in bottom nav, but kept reachable by URL
                      so existing links (strength-card → back, old bookmarks) work. */}
                  <Route path="videos" element={<MyVideos />} />
                  <Route path="video/:videoId" element={<VideoPlayer />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="account" element={<PlayerAccount />} />
                  <Route path="settings" element={<PlayerSettings />} />
                  <Route path="referrals" element={<PlayerReferrals />} />
                  <Route path="academy-privacy/:academyId" element={<AcademyPrivacySettings />} />
                  {/* Player-viewing-another-player (Module 3) */}
                  <Route path="profile/:playerId" element={<PlayerViewProfile />} />
                  {/* Discovery onboarding step (Module 3) — shown once
                      to brand-new players right after profile save. */}
                  <Route path="discover" element={<DiscoverPlayers />} />
                </Route>
              </Route>
              </Route>

              {/* Scout routes — also native-only on the web build. */}
              <Route element={<PlatformGuard allow="native" />}>
              {/* Scout onboarding — auth + role gate only (they're completing onboarding here) */}
              <Route element={<RequireAuth role="scout" />}>
                <Route path="/scout/onboarding" element={<ScoutOnboarding />} />
              </Route>

              {/* Scout app routes — require completed onboarding */}
              <Route element={<RequireAuth role="scout" requireOnboarding />}>
                <Route path="/scout" element={<ScoutLayout />}>
                  <Route path="find" element={<ScoutFindTalent />} />
                  <Route path="shortlists" element={<ScoutShortlists />} />
                  {/* Feed of posts from shortlisted players (Module 3). */}
                  <Route path="feed" element={<ScoutFeed />} />
                  <Route path="compare" element={<ScoutCompare />} />
                  <Route path="agents" element={<AIAgents />} />
                  <Route path="connect" element={<ScoutConnect />} />
                  <Route path="saved" element={<SavedReports />} />
                  <Route path="athlete/:athleteId" element={<ScoutAthleteProfile />} />
                  <Route path="settings" element={<ScoutSettings />} />
                </Route>
              </Route>
              </Route>

              {/* Academy onboarding — auth + role gate only (they're completing onboarding here) */}
              <Route element={<RequireAuth role="academy" />}>
                <Route path="/academy/onboarding" element={<AcademyOnboarding />} />
              </Route>

              {/* Academy app routes — require completed onboarding.
                  Web and native use entirely separate layouts: web
                  mounts the desktop AcademyWebLayout shell with
                  sidebar navigation and table-first pages; native
                  keeps the mobile AcademyLayout (bottom-nav, phone
                  width). The two trees never overlap — only the
                  one matching the current platform is registered. */}
              <Route element={<RequireAuth role="academy" requireOnboarding />}>
                {isWebPlatform() ? (
                  <Route path="/academy" element={<AcademyWebLayout />}>
                    <Route index element={<AcademyDashboardWeb />} />
                    {/* Backwards-compat alias: anything still linking
                        to /academy/home (mobile muscle memory) lands
                        on the new dashboard. */}
                    <Route path="home" element={<Navigate to="/academy" replace />} />
                    <Route path="players" element={<AcademyPlayersWeb />} />
                    <Route path="players/:playerId" element={<AcademyPlayerProfile />} />
                    {/* Web Sessions tab removed — "Add Session" now lives on
                        the Reports page. Redirect old links there. (Mobile
                        sessions are unaffected; they use AcademyLayout.) */}
                    <Route path="sessions" element={<Navigate to="/academy/reports" replace />} />
                    {/* Player-submitted video approvals queue. */}
                    <Route path="approvals" element={<VideoApprovalsPanel />} />
                    <Route path="reports" element={<AcademyReportsWeb />} />
                    {/* Per-player report detail — video + strength card +
                        all training sessions. Opened from a Reports folder. */}
                    <Route path="reports/:playerId" element={<PlayerReportDetail />} />
                    {/* Tournaments (formerly Trials). Old /trials links
                        redirect here so bookmarks keep working. */}
                    <Route path="promote" element={<AcademyPromoteWeb />} />
                    <Route path="tournaments" element={<AcademyTournamentsWeb />} />
                    <Route path="tournaments/:tournamentId" element={<TournamentDetailWeb />} />
                    <Route path="tournaments/:tournamentId/matches/:matchId" element={<MatchAnalysisWeb />} />
                    <Route path="trials" element={<Navigate to="/academy/tournaments" replace />} />
                    <Route path="settings" element={<AcademySettingsWeb />} />
                    <Route path="billing" element={<AcademyBillingWeb />} />
                    {/* Ask SportsBio — dashboard-tab feature. The chat
                        surface mounts directly under AcademyWebLayout
                        (no AI Lab sub-nav chrome) and is reached from
                        the "Ask SportsBio" Quick Action card on the
                        dashboard. /academy/ailab/ask redirects here
                        so any saved tab / bookmark from the prior
                        location still works. */}
                    <Route path="ask" element={<AILabAskSportsBio />} />
                    {/* AI Lab — nested under the web academy shell so
                        the sidebar + header chrome stay rendered. The
                        ailab layout brings its own dark theme + sub-nav
                        inside the content area. Selected-player state
                        is held in AILabWebLayout and shared with the
                        leaf pages via useOutletContext (NOT in the URL),
                        so flipping between Bowling / Batting / Radar /
                        Comparison preserves the choice. Index redirects
                        to /bowling so the lab always opens on a
                        concrete surface. */}
                    <Route path="ailab" element={<AILabWebLayout />}>
                      <Route index element={<Navigate to="bowling" replace />} />
                      <Route path="bowling" element={<AILabBowlingWeb />} />
                      <Route path="batting" element={<AILabBattingWeb />} />
                      <Route path="keeping" element={<AILabKeepingWeb />} />
                      <Route path="fielding" element={<AILabFieldingWeb />} />
                      <Route path="comparison" element={<AILabComparisonWeb />} />
                      <Route path="radar" element={<AILabRadarWeb />} />
                      {/* Backwards-compat redirect — Ask SportsBio now
                          lives at /academy/ask. */}
                      <Route path="ask" element={<Navigate to="/academy/ask" replace />} />
                    </Route>
                    {/* Old singular /academy/player/:playerId path —
                        preserve so existing links + saved sessions
                        keep working inside the web shell. */}
                    <Route path="player/:playerId" element={<AcademyPlayerProfile />} />
                  </Route>
                ) : (
                  <>
                    <Route path="/academy" element={<AcademyLayout />}>
                      <Route path="home" element={<AcademyHome />} />
                      <Route path="reports" element={<AcademyReports />} />
                      {/* Tournaments (replaces Trials). The web tournament
                          pages are responsive, so they render inside the
                          mobile bottom-nav shell too. Old /trials links
                          redirect here. */}
                      <Route path="tournaments" element={<AcademyTournamentsWeb />} />
                      <Route path="tournaments/:tournamentId" element={<TournamentDetailWeb />} />
                      <Route path="tournaments/:tournamentId/matches/:matchId" element={<MatchAnalysisWeb />} />
                      <Route path="trials" element={<Navigate to="/academy/tournaments" replace />} />
                      <Route path="promote" element={<AcademyPromoteMobile />} />
                      <Route path="tab" element={<AcademyTab />} />
                      {/* AI Lab grid home — bottom-nav stays visible. */}
                      <Route path="ailab" element={<AILabMobile />} />
                    </Route>
                    {/* Full-screen academy routes (outside the bottom-nav layout).
                        AI Lab feature screens are push-navigation full-screen
                        pages — back button returns to /academy/ailab. */}
                    <Route path="/academy/add-video" element={<AcademyAddVideo />} />
                    {/* Player-submitted video approvals — full-screen, reached
                        from the academy notifications panel. */}
                    <Route path="/academy/approvals" element={<VideoApprovalsMobile />} />
                    <Route path="/academy/player/:playerId" element={<AcademyPlayerProfile />} />
                    <Route path="/academy/ailab/bowling"    element={<AILabBowlingMobile />} />
                    <Route path="/academy/ailab/batting"    element={<AILabBattingMobile />} />
                    <Route path="/academy/ailab/keeping"    element={<AILabKeepingMobile />} />
                    <Route path="/academy/ailab/fielding"   element={<AILabFieldingMobile />} />
                    <Route path="/academy/ailab/radar"      element={<AILabRadarMobile />} />
                    <Route path="/academy/ailab/comparison" element={<AILabComparisonMobile />} />
                    <Route path="/academy/ailab/ask"        element={<AILabAskSportsBioMobile />} />
                  </>
                )}
              </Route>

              {/* Parent routes — accessible on BOTH web and native (the
                  only persona besides Academy that may use the web).
                  ParentAuth + ParentOnboarding are auth-flow screens so
                  they sit outside the RequireAuth gate (ParentAuth IS
                  the auth flow; ParentOnboarding requires auth and
                  enforces it inline). */}
              <Route path="/parent/auth" element={<ParentAuth />} />
              <Route element={<RequireAuth role="parent" />}>
                <Route path="/parent/onboarding" element={<ParentOnboarding />} />
                {/* Web parents land on the sidebar portal — section
                    switching happens inside ParentPortalWeb via
                    ?section=. Native parents use the bottom-nav layout
                    with nested routes per tab. */}
                <Route
                  path="/parent"
                  element={
                    isWebPlatform() ? <ParentPortalWeb /> : <Navigate to="/parent/home" replace />
                  }
                />
                <Route element={isWebPlatform() ? <Navigate to="/parent" replace /> : <ParentLayout />}>
                  <Route path="/parent/home" element={<ParentHome />} />
                  <Route path="/parent/videos" element={<ParentVideos />} />
                  <Route path="/parent/progress" element={<ParentProgress />} />
                  <Route path="/parent/notes" element={<ParentNotes />} />
                  <Route path="/parent/reports" element={<ParentReports />} />
                  <Route path="/parent/settings" element={<ParentSettings />} />
                </Route>
              </Route>

              {/* Pricing — the route is shared but dispatches by
                  platform. WEB serves the public marketing PricingPage
                  (no auth required) which is the conversion surface for
                  new academies landing on sportsbio.online. NATIVE keeps
                  the in-app Pricing surface that the player/scout
                  upgrade flows already link to. */}
              <Route
                path="/pricing"
                element={isWebPlatform() ? <PricingPage /> : <Pricing />}
              />

              {/* Web payment pages — WEB ONLY, and public.
                  PlatformGuard allow="web" is the mirror of the
                  allow="native" guards above: these two routes must never
                  render inside the Android app, because Google Play
                  forbids steering users to external payment from within
                  it. They sit at the top level, outside RequireAuth and
                  outside the native guard, so a player or an anonymous
                  visitor following a personalised link reaches them —
                  /upgrade does its own sign-in rather than bouncing to
                  /auth, which forces the Academy persona on web. */}
              <Route element={<PlatformGuard allow="web" />}>
                <Route path="/upgrade" element={<UpgradePage />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
              </Route>

              {/* Admin — WEB ONLY, signed in, and allowlisted. The page
                  renders the 404 for non-admins; the admin-billing-mode
                  Edge Function enforces ADMIN_USER_IDS server-side. */}
              <Route element={<PlatformGuard allow="web" />}>
                <Route element={<RequireAuth />}>
                  <Route path="/admin/billing" element={<AdminBilling />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ExitConfirmDialog />
          </AppShell>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
